/**
 * @file Digest Service
 * @description API endpoints for daily digest report operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';

/**
 * Activity counts in a digest
 */
export interface DigestActivityCounts {
  decisions: number;
  status_changes: number;
  new_investments: number;
  comments: number;
  active_investments: number;
  at_risk_items: number;
}

/**
 * Digest period
 */
export interface DigestPeriod {
  start: string;
  end: string;
}

/**
 * Decision activity in digest
 */
export interface DigestDecision {
  investment_decision_id: number;
  investment_id: number;
  investment_title: string;
  investment_status: string;
  decision_rationale: string | null;
  decision_maker_name: string;
  decision_maker_email: string;
  decision_date: string;
  reconsider_date: string | null;
}

/**
 * Status change activity in digest
 */
export interface DigestStatusChange {
  status_history_id: number;
  investment_id: number;
  investment_title: string;
  previous_status: string | null;
  new_status: string;
  changed_by: string;
  changed_at: string;
  notes: string | null;
}

/**
 * New investment activity in digest
 */
export interface DigestNewInvestment {
  investment_id: number;
  title: string;
  status: string;
  submitted_date: string;
  prime_objective: string | null;
}

/**
 * Comment activity in digest
 */
export interface DigestComment {
  investment_comment_id: number;
  investment_id: number;
  investment_title: string;
  comment: string;
  comment_by_name: string;
  comment_by_email: string;
  comment_date: string;
}

/**
 * Active investment in digest
 */
export interface DigestActiveInvestment {
  investment_id: number;
  title: string;
  status: string;
  status_changed_at: string | null;
  portco_name: string | null;
  segment_name: string | null;
}

/**
 * At-risk roadmap item in digest
 */
export interface DigestAtRiskItem {
  roadmap_item_id: number;
  investment_id: number;
  investment_title: string;
  roadmap_title: string;
  theme_title: string;
  start_date: string;
  end_date: string;
  delivery_date: string | null;
  is_committed: boolean;
  estimated_effort_days: number;
  estimated_remaining_days: number;
  risk_reason: string;
}

/**
 * Date pushout in digest
 */
export interface DigestDatePushout {
  roadmap_item_id: number;
  investment_title: string;
  roadmap_title: string;
  field_changed: string;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
  changed_by: string | null;
}

/**
 * Digest sections containing activity details
 */
export interface DigestSections {
  decisions: DigestDecision[];
  status_changes: DigestStatusChange[];
  new_investments: DigestNewInvestment[];
  comments: DigestComment[];
  active_investments: DigestActiveInvestment[];
  at_risk_items: DigestAtRiskItem[];
  date_pushouts: DigestDatePushout[];
}

/**
 * Digest summary with narrative and sections
 */
export interface DigestSummary {
  executive_narrative: string;
  sections: DigestSections;
}

/**
 * Generate digest request DTO
 */
export interface GenerateDigestDto {
  since?: string;
  format?: 'full' | 'summary_only' | 'json';
}

/**
 * Generate digest response
 */
export interface GenerateDigestResponse {
  digest_id: number;
  generated_at: string;
  period: DigestPeriod;
  counts: DigestActivityCounts;
  summary?: DigestSummary;
  html?: string;
}

/**
 * Digest history item
 */
export interface DigestHistoryItem {
  id: number;
  generated_at: string;
  generated_by: string;
  period: DigestPeriod;
  counts: DigestActivityCounts | null;
}

/**
 * Full digest record
 */
export interface DigestRecord {
  id: number;
  generated_at: string;
  generated_by: string;
  period: DigestPeriod;
  summary_text: string | null;
  counts: DigestActivityCounts | null;
  sections?: DigestSections;
}

/**
 * Digest Service
 */
class DigestService {
  private basePath = '/admin/digest';

  /**
   * Generate a new digest report
   */
  async generate(data?: GenerateDigestDto): Promise<ApiResponse<GenerateDigestResponse>> {
    return apiClient.post<GenerateDigestResponse>(
      `${this.basePath}/generate`,
      data || {}
    );
  }

  /**
   * Get digest history
   */
  async getHistory(limit?: number): Promise<ApiResponse<DigestHistoryItem[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return apiClient.get<DigestHistoryItem[]>(`${this.basePath}/history${params}`);
  }

  /**
   * Get a specific digest by ID
   */
  async getById(id: number): Promise<ApiResponse<DigestRecord>> {
    return apiClient.get<DigestRecord>(`${this.basePath}/${id}`);
  }

  /**
   * Get the latest digest
   */
  async getLatest(): Promise<ApiResponse<DigestRecord | null>> {
    return apiClient.get<DigestRecord | null>(`${this.basePath}/latest`);
  }
}

export const digestService = new DigestService();
